// src/modules/i18n.ts — Faza D.C.2 i18n framework
// P3.2 strict TS migration (2026-04-29).

import { atom } from "nanostores";
import type { WritableAtom } from "nanostores";

export type Locale = "pl" | "en" | "de";
export type TranslationDict = Record<string, string>;
export type TranslationParams = Record<string, string | number>;

interface I18nAPI {
  t: typeof t;
  tStatus: typeof tStatus;
  setLocale: typeof setLocale;
  loadTranslations: typeof loadTranslations;
  getSupportedLocales: typeof getSupportedLocales;
  getMissingKeys: typeof getMissingKeys;
  $locale: WritableAtom<Locale>;
}

const _DEFAULT_LOCALE: Locale = "pl";
const _SUPPORTED: readonly Locale[] = ["pl", "en", "de"] as const;

function _isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (_SUPPORTED as readonly string[]).includes(value);
}

// Reactive store dla aktualnego locale
export const $locale: WritableAtom<Locale> = atom<Locale>(
  (() => {
    try {
      const saved = localStorage.getItem("sellbase-locale");
      if (saved && _isLocale(saved)) return saved;
      const browserLocale = (navigator.language || "").slice(0, 2).toLowerCase();
      if (_isLocale(browserLocale)) return browserLocale;
    } catch (_e: unknown) {
      // localStorage may be blocked (private mode)
    }
    return _DEFAULT_LOCALE;
  })(),
);

// Persist do localStorage
$locale.subscribe((locale: Locale) => {
  try {
    localStorage.setItem("sellbase-locale", locale);
  } catch (_e: unknown) {
    // best-effort — ignore quota / blocked errors
  }
});

// In-memory translation cache: { 'pl': {...}, 'en': {...} }
const _translations: Partial<Record<Locale, TranslationDict>> = {};

// Missing keys tracker (dev tool — log + window.__sbI18nMissing)
const _missingKeys = new Set<string>();

/**
 * Translate key. Fallback to key if no translation found.
 *
 * @example
 *   t('orders.list.title') → 'Zamówienia'
 *   t('orders.empty', {count: 5}) → 'Brak 5 zamówień'
 *
 * Faza L.7.d — pluralization. Gdy `params.count` jest podane, t() szuka kluczy w kolejności:
 *   1. `key.<pluralCategory>` (np. orders.count.few dla 2-4 PL)
 *   2. `key` (fallback dla "other")
 * Plural categories per locale (CLDR):
 *   - pl: one, few, many, other
 *   - en: one, other
 *   - de: one, other
 */
export function t(key: string, params?: TranslationParams): string {
  const locale = $locale.get();
  const dict = _translations[locale] || {};

  // Faza L.7.d — pluralization. Gdy params.count podane, próbujemy klucz z plural suffix najpierw.
  let lookupKey = key;
  if (params && typeof params.count === "number") {
    try {
      const pr = new Intl.PluralRules(locale);
      const cat = pr.select(params.count); // "one" | "few" | "many" | "other" | "two" | "zero"
      const candidate = `${key}.${cat}`;
      if (dict[candidate] !== undefined) {
        lookupKey = candidate;
      } else if (locale !== _DEFAULT_LOCALE) {
        const defaultDict = _translations[_DEFAULT_LOCALE];
        if (defaultDict && defaultDict[candidate] !== undefined) {
          lookupKey = candidate;
        }
      }
    } catch (_e: unknown) {
      // Intl.PluralRules niedostępne (very old browser) — fallback do plain key
    }
  }

  let value: string | undefined = dict[lookupKey];

  if (value === undefined) {
    // Fallback do default locale
    if (locale !== _DEFAULT_LOCALE) {
      const defaultDict = _translations[_DEFAULT_LOCALE];
      if (defaultDict) value = defaultDict[lookupKey];
    }
  }
  // Drugi fallback — gdy lookupKey nie ma, spróbuj plain key bez plural suffix
  if (value === undefined && lookupKey !== key) {
    value = dict[key];
    if (value === undefined && locale !== _DEFAULT_LOCALE) {
      const defaultDict = _translations[_DEFAULT_LOCALE];
      if (defaultDict) value = defaultDict[key];
    }
  }

  if (value === undefined) {
    // Track missing keys (dev) — using lookup key (z plural suffix gdy applicable)
    if (!_missingKeys.has(lookupKey)) {
      _missingKeys.add(lookupKey);
      if (typeof console !== "undefined" && console.warn) {
        console.warn("[i18n] missing key:", lookupKey, "(locale:", locale + ")");
      }
    }
    value = lookupKey; // fallback: key as text
  }

  // Interpolation: "Brak {count} zamówień" → "Brak 5 zamówień"
  if (params && value.indexOf("{") !== -1) {
    let result = value;
    for (const k of Object.keys(params)) {
      result = result.replace(new RegExp("\\{" + k + "\\}", "g"), String(params[k]));
    }
    return result;
  }

  return value;
}

/**
 * Załaduj słownik dla danego locale.
 */
export function loadTranslations(locale: Locale, dict: TranslationDict): void {
  _translations[locale] = Object.assign(_translations[locale] || {}, dict);
}

/**
 * Przełącz aktualny locale.
 */
export function setLocale(locale: string): void {
  if (!_isLocale(locale)) {
    console.warn("[i18n] unsupported locale:", locale);
    return;
  }
  $locale.set(locale);
}

/**
 * Lista wspieranych locale.
 */
export function getSupportedLocales(): Locale[] {
  return [..._SUPPORTED];
}

/**
 * Diagnostyka: missing keys.
 */
export function getMissingKeys(): string[] {
  return Array.from(_missingKeys);
}

declare global {
  interface Window {
    __sbI18n?: I18nAPI;
  }
}

/**
 * Faza L.7.i — status label translation z fallback do raw label.
 *
 * @example
 *   tStatus('order', 'new', 'Nowe') → 'New' (en) / 'Nowe' (pl) / 'Neu' (de)
 *   tStatus('payment', 'paid') → 'Paid' (en) / 'Opłacone' (pl) / 'Bezahlt' (de)
 *   tStatus('order', 'custom_user_status', 'Mój status') → 'Mój status' (raw, brak klucza)
 *
 * Categories (zdefiniowane w status.* keys w JSON):
 *   - order: new/processing/packed/ready_to_ship/shipped/ready_for_pickup/delivered/cancelled/returned
 *   - payment: paid/partially_paid/pending/partially_refunded/refunded
 *   - customer: standard/staly/hurt/vip/czarna_lista
 *   - ksef: NOT_APPLICABLE/PENDING_SEND/QUEUED/PROCESSING/ACCEPTED/REJECTED/ERROR
 *   - invoice: draft/issued/approved/sent/paid_partial/paid/overdue/cancelled
 *   - invoice_type: invoice/proforma/receipt/correction/wdt/oss
 *   - ticket: new/assigned/in_progress/.../closed_rejected
 *   - ticket_type: standard_message/allegro_discussion/complaint
 *   - issue: DISPUTE_X / CLAIM_X
 */
// Faza L.7.i.b — alias mapping per category dla legacy keys (np. backend zwraca polskie nazwy
// jako key z `key: name.toLowerCase().replace(/\s+/g,'_')`. Mapujemy je na canonical English keys.
const _STATUS_ALIASES: Record<string, Record<string, string>> = {
  order: {
    "nowe": "new",
    "w_realizacji": "processing",
    "spakowane": "packed",
    "do_wysłania": "ready_to_ship",
    "do_wyslania": "ready_to_ship",
    "wysłane": "shipped",
    "wyslane": "shipped",
    "do_odbioru": "ready_for_pickup",
    "odebrane": "delivered",
    "anulowane": "cancelled",
    "zwrot": "returned",
  },
  payment: {
    "opłacone": "paid",
    "oplacone": "paid",
    "częściowo_opłacone": "partially_paid",
    "czesciowo_oplacone": "partially_paid",
    "oczekuje": "pending",
    "częściowo_zwrócone": "partially_refunded",
    "zwrócone": "refunded",
    "zwrocone": "refunded",
    "pobranie": "cod",
  },
  shipment: {
    "brak_przesyłki": "no_shipment",
    "brak_przesylki": "no_shipment",
    "brak": "no_shipment",
    "utworzona": "created",
    "nadana": "dispatched",
    "w_transporcie": "in_transit",
    "dostarczona": "delivered",
    "zwrócona": "returned",
    "anulowana": "cancelled",
  },
};

export function tStatus(category: string, key: string | null | undefined, fallback?: string): string {
  if (key === null || key === undefined || key === "") return fallback || "";
  // Resolve alias (PL legacy key → canonical EN key)
  const aliases = _STATUS_ALIASES[category];
  let resolved = String(key);
  if (aliases && aliases[resolved.toLowerCase()] !== undefined) {
    resolved = aliases[resolved.toLowerCase()];
  }
  const fullKey = `status.${category}.${resolved}`;
  const locale = $locale.get();
  const dict = _translations[locale] || {};
  if (dict[fullKey] !== undefined) return dict[fullKey];
  // Fallback do default locale
  if (locale !== _DEFAULT_LOCALE) {
    const defaultDict = _translations[_DEFAULT_LOCALE];
    if (defaultDict && defaultDict[fullKey] !== undefined) return defaultDict[fullKey];
  }
  // Brak klucza — zwróć raw fallback (np. user-defined custom status z backend)
  return fallback || String(key);
}

// Wystaw na window dla DevTools console + integration
if (typeof window !== "undefined") {
  window.__sbI18n = { t, tStatus, setLocale, loadTranslations, getSupportedLocales, getMissingKeys, $locale };
}

// Faza L.7 — translations externalizowane do src/i18n/{pl,en,de}.json (~80 keys per locale).
// 2026-07-09 (audyt perf): statycznie tylko PL (UI jest hardcoded PL) — en/de przez
// dynamic import przy setLocale; ~29KB min mniej w main.js na każdym starcie.
import _plDict from "../i18n/pl.json" with { type: "json" };

loadTranslations("pl", _plDict as TranslationDict);

const _lazyDictLoaders: Record<string, () => Promise<{ default: TranslationDict }>> = {
  en: () => import("../i18n/en.json", { with: { type: "json" } }) as Promise<{ default: TranslationDict }>,
  de: () => import("../i18n/de.json", { with: { type: "json" } }) as Promise<{ default: TranslationDict }>,
};
const _lazyDictLoaded: Record<string, boolean> = {};

$locale.subscribe(function (loc: string) {
  const loader = _lazyDictLoaders[loc];
  if (!loader || _lazyDictLoaded[loc]) return;
  _lazyDictLoaded[loc] = true;
  loader().then(function (mod) {
    loadTranslations(loc as Locale, (mod && mod.default) || ({} as TranslationDict));
    // re-emit żeby subskrybenci przerysowali teksty już z załadowanym słownikiem
    $locale.set(loc as Locale);
  }).catch(function (e) {
    _lazyDictLoaded[loc] = false;
    console.warn("[i18n] lazy dict load failed:", loc, e);
  });
});
